import axios, { AxiosError, type AxiosInstance } from 'axios'

/**
 * Centralized HTTP client. All API access goes through this instance so base URL,
 * headers, and error handling live in exactly one place.
 *
 * Base URL resolution:
 *  - `VITE_API_BASE_URL` when set (e.g. a separate API host).
 *  - Otherwise empty string => same-origin relative requests. In Docker the nginx
 *    frontend proxies `/api` to the backend; in `npm run dev` the Vite proxy does.
 */
const baseURL = import.meta.env.VITE_API_BASE_URL ?? ''

export const httpClient: AxiosInstance = axios.create({
  baseURL,
  timeout: 20000,
  // Send the auth cookie with every request (needed when the API is on another origin;
  // harmless same-origin).
  withCredentials: true,
  headers: {
    Accept: 'application/json'
  }
})

// Global handler invoked when the session is rejected (401) on a normal API call, so the
// app can drop to the login screen. Set once at startup; kept here to avoid import cycles.
let onUnauthorized: (() => void) | null = null

export function setUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler
}

httpClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      const url = error.config?.url ?? ''
      // The session probe and the login call handle their own 401s.
      if (!url.includes('/api/auth/me') && !url.includes('/api/auth/login')) {
        onUnauthorized?.()
      }
    }
    return Promise.reject(error)
  }
)

/** A normalized, user-safe error surfaced to the UI. */
export interface ApiError {
  status: number | null
  title: string
  detail?: string
}

export function toApiError(error: unknown): ApiError {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{ title?: string; detail?: string }>

    if (axiosError.response) {
      return {
        status: axiosError.response.status,
        title: axiosError.response.data?.title ?? 'Request failed',
        detail: axiosError.response.data?.detail
      }
    }

    return {
      status: null,
      title: 'Unable to connect to the server.',
      detail: axiosError.message
    }
  }

  return { status: null, title: 'Unexpected error' }
}
